import { useState } from 'react';

type Phase = 'ready' | 'contact' | 'sending' | 'sent' | 'error';

type ContactForm = {
  name: string;
  email: string;
  phone: string;
  message: string;
};

type ExportModalProps = {
  pdfUrl: string;
  captures: string[];
  onClose: () => void;
  onSubmitContact: (form: ContactForm) => Promise<void>;
  initialMessage?: string;
};

const VIEW_LABELS = ['Avant', 'Droite', 'Arrière', 'Gauche'];

export default function ExportModal({ pdfUrl, captures, onClose, onSubmitContact, initialMessage = '' }: ExportModalProps) {
  const [phase, setPhase] = useState<Phase>('ready');
  const [form, setForm] = useState<ContactForm>({ name: '', email: '', phone: '', message: initialMessage });
  const [formError, setFormError] = useState<string | null>(null);

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = `indyanna_creation_${new Date().toISOString().slice(0, 10)}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.name.trim()) {
      setFormError('Veuillez renseigner votre nom.');
      return;
    }
    if (!emailRegex.test(form.email.trim())) {
      setFormError('Adresse email invalide.');
      return;
    }
    setFormError(null);
    setPhase('sending');
    try {
      await onSubmitContact(form);
      setPhase('sent');
    } catch {
      setPhase('error');
    }
  };

  return (
    <div className="export-modal-overlay" role="dialog" aria-modal="true" aria-label="Création terminée">
      <div className="export-modal-card">

        {/* ── HEADER ── */}
        <div className="export-modal-header">
          <img src="/assets/IndyannaStudio_Logo.png" alt="Indyanna Studio" className="export-modal-logo" />
          <button
            className="export-modal-close"
            type="button"
            onClick={onClose}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>

        {/* ── PHASE: READY ── */}
        {phase === 'ready' && (
          <>
            <div className="export-modal-hero">
              <div className="export-modal-hero-badge">Création terminée</div>
              <h2 className="export-modal-title">Votre t-shirt est prêt&nbsp;!</h2>
              <p className="export-modal-subtitle">
                Voici un aperçu de votre création sous toutes ses faces. Téléchargez
                votre fiche PDF ou transmettez-la directement à notre équipe.
              </p>
            </div>

            <div className="export-modal-captures">
              {captures.map((src, i) => (
                <div key={i} className="export-modal-capture">
                  <img src={src} alt={`Vue ${VIEW_LABELS[i]}`} />
                  <span className="export-modal-capture-label">{VIEW_LABELS[i] ?? `Vue ${i + 1}`}</span>
                </div>
              ))}
            </div>

            <div className="export-modal-actions">
              <button
                type="button"
                className="export-modal-btn export-modal-btn--secondary"
                onClick={handleDownload}
              >
                <span className="export-modal-btn-icon">
                  <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 3v9m0 0-3-3m3 3 3-3M4 14v.5A2.5 2.5 0 0 0 6.5 17h7a2.5 2.5 0 0 0 2.5-2.5V14" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                Télécharger le PDF
              </button>
              <button
                type="button"
                className="export-modal-btn export-modal-btn--primary"
                onClick={() => setPhase('contact')}
              >
                <span className="export-modal-btn-icon">
                  <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 5.5A2.5 2.5 0 0 1 4.5 3h11A2.5 2.5 0 0 1 18 5.5v9A2.5 2.5 0 0 1 15.5 17h-11A2.5 2.5 0 0 1 2 14.5v-9Z" stroke="currentColor" strokeWidth="1.7"/>
                    <path d="m2 6 8 5.5L18 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
                Transmettre à Indyanna Pub
              </button>
            </div>
            <p className="export-modal-hint">
              Notre équipe vous contactera pour confirmer votre commande et vous proposer un devis personnalisé.
            </p>
          </>
        )}

        {/* ── PHASE: CONTACT FORM ── */}
        {phase === 'contact' && (
          <>
            <div className="export-modal-hero">
              <div className="export-modal-hero-badge export-modal-hero-badge--contact">Mise en relation</div>
              <h2 className="export-modal-title">Transmettre ma création</h2>
              <p className="export-modal-subtitle">
                Renseignez vos coordonnées. Notre équipe vous recontactera dans les plus brefs délais
                pour finaliser votre commande.
              </p>
            </div>

            <div className="export-modal-form">
              <div className="export-modal-form-row">
                <div className="export-modal-field">
                  <label className="export-modal-label" htmlFor="em-name">Nom complet *</label>
                  <input
                    id="em-name"
                    className="export-modal-input"
                    type="text"
                    placeholder="Jean Dupont"
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    autoComplete="name"
                  />
                </div>
                <div className="export-modal-field">
                  <label className="export-modal-label" htmlFor="em-email">Adresse email *</label>
                  <input
                    id="em-email"
                    className="export-modal-input"
                    type="email"
                    placeholder="jean@exemple.fr"
                    value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    autoComplete="email"
                  />
                </div>
              </div>
              <div className="export-modal-field">
                <label className="export-modal-label" htmlFor="em-phone">Téléphone <span className="export-modal-optional">(facultatif)</span></label>
                <input
                  id="em-phone"
                  className="export-modal-input"
                  type="tel"
                  placeholder="+33 6 00 00 00 00"
                  value={form.phone}
                  onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  autoComplete="tel"
                />
              </div>
              <div className="export-modal-field">
                <label className="export-modal-label" htmlFor="em-message">Message <span className="export-modal-optional">(facultatif)</span></label>
                <textarea
                  id="em-message"
                  className="export-modal-input export-modal-textarea"
                  placeholder="Quantité souhaitée, délai, questions…"
                  value={form.message}
                  onChange={e => setForm(p => ({ ...p, message: e.target.value }))}
                  rows={3}
                />
              </div>

              {formError && <p className="export-modal-error">{formError}</p>}

              <div className="export-modal-form-actions">
                <button
                  type="button"
                  className="export-modal-btn export-modal-btn--ghost"
                  onClick={() => { setPhase('ready'); setFormError(null); }}
                >
                  Retour
                </button>
                <button
                  type="button"
                  className="export-modal-btn export-modal-btn--primary"
                  onClick={handleSubmit}
                >
                  Envoyer ma création
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── PHASE: SENDING ── */}
        {phase === 'sending' && (
          <div className="export-modal-state">
            <div className="export-modal-spinner" aria-hidden="true" />
            <p className="export-modal-state-title">Envoi en cours…</p>
            <p className="export-modal-state-sub">Transmission de votre création à notre équipe.</p>
          </div>
        )}

        {/* ── PHASE: SENT ── */}
        {phase === 'sent' && (
          <div className="export-modal-state">
            <div className="export-modal-check" aria-hidden="true">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="22" stroke="#ef7218" strokeWidth="2.5"/>
                <path d="M13 25l8 8L35 16" stroke="#ef7218" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <p className="export-modal-state-title">Message envoyé !</p>
            <p className="export-modal-state-sub">
              Notre équipe a bien reçu votre création et vous contactera très prochainement.
            </p>
            <div className="export-modal-state-actions">
              <button
                type="button"
                className="export-modal-btn export-modal-btn--secondary"
                onClick={handleDownload}
              >
                Télécharger mon PDF
              </button>
              <button
                type="button"
                className="export-modal-btn export-modal-btn--ghost"
                onClick={onClose}
              >
                Fermer
              </button>
            </div>
          </div>
        )}

        {/* ── PHASE: ERROR ── */}
        {phase === 'error' && (
          <div className="export-modal-state">
            <div className="export-modal-error-icon" aria-hidden="true">
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="22" stroke="#ef4444" strokeWidth="2.5"/>
                <path d="M16 16l16 16M32 16L16 32" stroke="#ef4444" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
            <p className="export-modal-state-title">Envoi impossible</p>
            <p className="export-modal-state-sub">
              Une erreur est survenue. Vous pouvez réessayer ou nous contacter directement.
            </p>
            <div className="export-modal-state-actions">
              <button
                type="button"
                className="export-modal-btn export-modal-btn--primary"
                onClick={() => setPhase('contact')}
              >
                Réessayer
              </button>
              <button
                type="button"
                className="export-modal-btn export-modal-btn--secondary"
                onClick={handleDownload}
              >
                Télécharger le PDF
              </button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
